/**
 * Candidates for the `Add Lighthouse Account` dialog — `SearchAccounts`, driven by a Cloudscape
 * Multiselect's `onLoadItems` (DAT-9086).
 *
 * The candidates are exactly the accounts `PutAccountFlag(LIGHTHOUSE)` accepts for this caller:
 * the account's OWN hierarchy in the caller's industries, the write's own check. So a pick does
 * not come back 403, and "no result" means "not in your industries", not "not found".
 *
 * Cloudscape facts this is built around, all read off its source rather than its docs (see
 * memory `reference_cloudscape_select_load_items`):
 *   - The filter input is already delayed ~200ms before `onLoadItems` fires; DEBOUNCE_MS sits on
 *     top of that, as usePipelineFilterOptions does on the Tagged Pipeline page, so the two
 *     dropdowns feel the same.
 *   - `onLoadItems` is NOT fired again for the same text. After an industry change the dropdown
 *     would never ask again, so this hook re-fetches page one ITSELF with the last term.
 *   - The error state's retry fires `samePage: true`: re-run the request that failed, which is
 *     the page the token still points at — not the next one.
 *   - Load-more fires only while `status` is `pending`, which is therefore what "more to come"
 *     is reported as.
 *
 * Every request carries a sequence number and only the latest one's answer lands, so a slower
 * earlier search cannot overwrite a newer one.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AccountCandidate } from '@amzn/ivthub-client';
import { getIvtClient, SearchAccountsCommand } from '../../client/IvtHubClient';

export const DEBOUNCE_MS = 300;

/** The service's cap on `searchTerm`; longer is a 400, so the term is clipped rather than sent. */
const MAX_TERM = 512;

export type AccountSearchStatus = 'pending' | 'loading' | 'finished' | 'error';

/** The Multiselect `onLoadItems` detail, restated so this file does not depend on the component's types. */
export interface LoadItemsDetail {
  filteringText: string;
  firstPage: boolean;
  samePage: boolean;
}

export interface AccountSearchState {
  candidates: AccountCandidate[];
  status: AccountSearchStatus;
  onLoadItems: (e: { detail: LoadItemsDetail }) => void;
}

const httpStatusOf = (e: unknown) =>
  (e as { $metadata?: { httpStatusCode?: number } } | null)?.$metadata?.httpStatusCode;

/**
 * A 400 for a token that belongs to another search, told apart by the message the contract names.
 *
 * ⚠️ Never by exception NAME. The service raises two for one status — Coral's `ValidationException`
 * for the model's limits, its own `InvalidInputException` for a stale token or input the database
 * refuses — and the contract says to branch on the 400 alone.
 */
const isStaleToken = (e: unknown) =>
  httpStatusOf(e) === 400 && /does not match the current search/.test((e as Error | null)?.message ?? '');

/**
 * @param industry the page's industry selector: `null` = every industry the caller may see, sent
 *   by OMITTING `industries` (the service intersects whatever arrives with the permitted set).
 */
export function useAccountSearch(industry: string | null): AccountSearchState {
  const [candidates, setCandidates] = useState<AccountCandidate[]>([]);
  const [status, setStatus] = useState<AccountSearchStatus>('pending');

  const seqRef = useRef(0);
  /** What fetches the NEXT page of the current search; absent on page one and after the last. */
  const tokenRef = useRef<string | undefined>(undefined);
  /** The term the list currently belongs to. `null` until the dropdown first asks for anything. */
  const termRef = useRef<string | null>(null);
  /** Whether the request that just failed was page one — what a `samePage` retry must re-run. */
  const failedFirstPageRef = useRef(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Read at send time rather than captured, so a timer that outlives an industry change cannot
  // search the old industry.
  const industryRef = useRef(industry);
  industryRef.current = industry;

  const fetchPage = useCallback(async (term: string, firstPage: boolean): Promise<void> => {
    const seq = ++seqRef.current;
    const token = firstPage ? undefined : tokenRef.current;
    const scope = industryRef.current;
    setStatus('loading');
    try {
      const out = await getIvtClient().send(
        new SearchAccountsCommand({
          ...(term !== '' && { searchTerm: term }),
          ...(scope !== null && { industries: [scope] }),
          ...(token !== undefined && { nextToken: token }),
        }),
      );
      if (seq !== seqRef.current) return;
      tokenRef.current = out.nextToken || undefined;
      const page = out.items ?? [];
      setCandidates((prev) => {
        if (firstPage) return page;
        const seen = new Set(prev.map((c) => c.accountId));
        return [...prev, ...page.filter((c) => !seen.has(c.accountId))];
      });
      setStatus(tokenRef.current !== undefined ? 'pending' : 'finished');
    } catch (e) {
      if (seq !== seqRef.current) return;
      if (!firstPage && isStaleToken(e)) {
        // The token outlived its search. The contract's remedy: drop it and start over.
        tokenRef.current = undefined;
        void fetchPage(term, true);
        return;
      }
      if (!firstPage && httpStatusOf(e) === 400) {
        // The other load-more 400 the contract names: a name long enough (~240 CJK characters)
        // makes the next-page token too long to send back. Treated as the end of the list, as
        // the contract says to — an error state here would offer a retry that fails the same way.
        tokenRef.current = undefined;
        setStatus('finished');
        return;
      }
      failedFirstPageRef.current = firstPage;
      setStatus('error');
    }
  }, []);

  // An industry change is a new search: drop the rows, the token, any pending keystroke and any
  // request in flight — then ask again, because the dropdown will not (see the header).
  // Defensive today: the dialog is mounted per opening and its overlay covers the page's industry
  // selector, so on the page this runs only on mount, where there is nothing to re-ask.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    seqRef.current++;
    tokenRef.current = undefined;
    setCandidates([]);
    setStatus('pending');
    if (termRef.current !== null) void fetchPage(termRef.current, true);
  }, [industry, fetchPage]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      // Anything still in flight must not land in an unmounted dialog.
      seqRef.current++;
    },
    [],
  );

  const onLoadItems = useCallback(
    ({ detail }: { detail: LoadItemsDetail }) => {
      if (detail.samePage) {
        void fetchPage(termRef.current ?? '', failedFirstPageRef.current);
        return;
      }
      if (detail.firstPage) {
        const term = detail.filteringText.trim().slice(0, MAX_TERM);
        termRef.current = term;
        tokenRef.current = undefined;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        // Invalidate the previous term's request NOW, not when the timer fires: its answer would
        // otherwise land during the debounce and show the old term's rows under the new text.
        seqRef.current++;
        setStatus('loading');
        debounceRef.current = setTimeout(() => void fetchPage(term, true), DEBOUNCE_MS);
        return;
      }
      // Load more. No debounce — it is a scroll, not a keystroke — and nothing to do without a
      // token, which is how "already at the end" reads.
      if (tokenRef.current !== undefined) void fetchPage(termRef.current ?? '', false);
    },
    [fetchPage],
  );

  return { candidates, status, onLoadItems };
}
